import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiClient } from '../services/api';
import { formatStripeAmount, formatDate } from '../utils/formatStripe';
import HubSpotInvoiceDetail from './HubSpotInvoiceDetail';
import type { BillingInvoiceSummary, BillingSubscriptionSummary } from '../types/api';

// Truncate a long Stripe object id for table display; the full value is kept in
// a `title` attribute by the caller.
function truncateId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 12)}…` : id;
}

// Map a Stripe invoice status to a Badge variant. paid → default (positive),
// uncollectible/void → destructive, everything else (open, draft) → secondary.
function invoiceStatusVariant(
  status: string | null | undefined,
): 'default' | 'secondary' | 'destructive' {
  switch (status) {
    case 'paid':
      return 'default';
    case 'uncollectible':
    case 'void':
      return 'destructive';
    default:
      return 'secondary';
  }
}

/**
 * Billing drill-down view for the HubSpot company/contact iframe modal.
 * Chrome-less: subscriptions table → invoice history → expandable invoice detail.
 */
const HubSpotBillingDetail: React.FC<{ recordType: 'company' | 'contact' }> = ({
  recordType,
}) => {
  const { recordId } = useParams<{ recordId: string }>();
  // Deep-link hints from the card's "View" button (?sub=&invoice=): pre-select
  // that subscription and auto-expand that invoice.
  const [searchParams] = useSearchParams();
  const deepInvoiceId = searchParams.get('invoice');
  const [selectedSubId, setSelectedSubId] = useState<string | null>(
    searchParams.get('sub'),
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['billing', recordType, recordId],
    queryFn: () => apiClient.getBillingContext(recordType, recordId!),
    enabled: !!recordId,
  });

  // If a refetch returns data that no longer contains the selected
  // subscription, clear the selection so the panel doesn't go stale.
  useEffect(() => {
    if (
      selectedSubId &&
      data &&
      !data.subscriptions.some((s) => s.stripe_subscription_id === selectedSubId)
    ) {
      setSelectedSubId(null);
    }
  }, [data, selectedSubId]);

  const handleClose = () => {
    window.parent.postMessage({ action: 'DONE' }, '*');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-4 space-y-3">
        <Alert variant="destructive">
          <AlertDescription>Failed to load billing details.</AlertDescription>
        </Alert>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const { subscriptions, standalone_invoices, warnings, truncated } = data;
  const selectedSub = subscriptions.find(
    (s) => s.stripe_subscription_id === selectedSubId,
  );

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold capitalize">{recordType} billing</h1>
        <Button variant="outline" size="sm" onClick={handleClose}>
          Close
        </Button>
      </div>

      {/* Warnings / truncation notice */}
      {(warnings.length > 0 || truncated) && (
        <Alert>
          <AlertTitle>Notice</AlertTitle>
          <AlertDescription>
            {warnings.length > 0 && (
              <ul className="list-disc pl-4 space-y-0.5">
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
            {truncated && (
              <div className="text-muted-foreground">Showing partial results.</div>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Subscriptions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Subscriptions ({subscriptions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {subscriptions.length === 0 ? (
            <div className="text-sm text-muted-foreground">No subscriptions found.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subscription</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Period ends</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Customer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscriptions.map((sub) => {
                  const isSelected = sub.stripe_subscription_id === selectedSubId;
                  const toggleSelection = () =>
                    setSelectedSubId(isSelected ? null : sub.stripe_subscription_id);
                  return (
                    <TableRow
                      key={sub.stripe_subscription_id}
                      className={`cursor-pointer ${isSelected ? 'bg-muted' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isSelected}
                      onClick={toggleSelection}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleSelection();
                        }
                      }}
                    >
                      <TableCell
                        className="font-mono text-xs"
                        title={sub.stripe_subscription_id}
                      >
                        {truncateId(sub.stripe_subscription_id)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={sub.status === 'active' ? 'default' : 'secondary'}>
                          {sub.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatStripeAmount(sub.amount, sub.currency)}
                        {sub.interval && (
                          <span className="text-muted-foreground">/{sub.interval}</span>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(sub.current_period_end)}</TableCell>
                      <TableCell>
                        <SourceBadge source={sub.source} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {sub.customer_email ?? '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Selected subscription: invoice history */}
      {selectedSub && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Invoice history — {selectedSub.stripe_subscription_id}
            </CardTitle>
            {selectedSub.product_names.length > 0 && (
              <div className="text-sm text-muted-foreground">
                {selectedSub.product_names.join(', ')}
              </div>
            )}
          </CardHeader>
          <CardContent>
            <SubscriptionInvoiceHistory
              subscriptionId={selectedSub.stripe_subscription_id}
              initialExpandedId={deepInvoiceId}
            />
          </CardContent>
        </Card>
      )}

      {/* Standalone invoices */}
      {standalone_invoices.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Standalone invoices ({standalone_invoices.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <InvoiceHistoryTable
              invoices={standalone_invoices}
              initialExpandedId={deepInvoiceId}
              showSource
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Copy for the automatch tooltip — a matched subscription is only a *potential*
// link the connector inferred from email evidence, not a confirmed one.
const AUTOMATCH_TOOLTIP =
  'Automatched: this is a potential link — the email addresses show evidence of matching.';

// Renders a subscription's Badge for how it was discovered. "synced" and
// "linked" are confirmed provenances (default / secondary badge). "matched" is
// an automatch — a heuristic, unconfirmed link — so it carries a tooltip
// spelling that out.
const SourceBadge: React.FC<{
  source: BillingSubscriptionSummary['source'] | BillingInvoiceSummary['source'];
}> = ({ source }) => {
  if (source === 'synced') {
    return <Badge variant="default">Synced</Badge>;
  }
  if (source === 'matched') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="secondary" className="cursor-help">
              Automatch
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{AUTOMATCH_TOOLTIP}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return <Badge variant="secondary">Linked</Badge>;
};

// Loads and renders the invoice history for a single subscription.
const SubscriptionInvoiceHistory: React.FC<{
  subscriptionId: string;
  initialExpandedId?: string | null;
}> = ({ subscriptionId, initialExpandedId }) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sub-invoices', subscriptionId],
    queryFn: () => apiClient.getSubscriptionInvoices(subscriptionId),
    enabled: !!subscriptionId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center py-2">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center gap-2 py-1">
        <span className="text-sm text-muted-foreground">Failed to load invoices.</span>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <InvoiceHistoryTable invoices={data.invoices} initialExpandedId={initialExpandedId} />
  );
};

// A table of invoices; each row expands to mount <InvoiceDetail /> for that invoice.
// `showSource` controls visibility of the Source column: only set true for standalone invoices,
// where the backend stamps provenance via _stamp_invoice_provenance. Per-subscription invoices
// never carry `source` in the backend dict, so defaulting to 'matched' would be misleading.
const InvoiceHistoryTable: React.FC<{
  invoices: BillingInvoiceSummary[];
  initialExpandedId?: string | null;
  showSource?: boolean;
}> = ({ invoices, initialExpandedId, showSource = false }) => {
  const [expandedId, setExpandedId] = useState<string | null>(initialExpandedId ?? null);

  if (invoices.length === 0) {
    return <div className="text-sm text-muted-foreground">No invoices.</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <TableHead>Number</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Amount due</TableHead>
          <TableHead>Due date</TableHead>
          <TableHead>Created</TableHead>
          {showSource && <TableHead>Source</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {invoices.map((inv) => {
          const isExpanded = inv.stripe_invoice_id === expandedId;
          const toggleExpanded = () =>
            setExpandedId(isExpanded ? null : inv.stripe_invoice_id);
          return (
            <React.Fragment key={inv.stripe_invoice_id}>
              <TableRow className="cursor-pointer" onClick={toggleExpanded}>
                <TableCell>
                  <button
                    type="button"
                    className="flex items-center justify-center rounded-sm p-0.5 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={isExpanded ? 'Collapse invoice' : 'Expand invoice'}
                    aria-expanded={isExpanded}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpanded();
                    }}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {inv.number ?? inv.stripe_invoice_id}
                </TableCell>
                <TableCell>
                  <Badge variant={invoiceStatusVariant(inv.status)}>
                    {inv.status ?? '—'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {formatStripeAmount(inv.amount_due, inv.currency)}
                </TableCell>
                <TableCell>{formatDate(inv.due_date)}</TableCell>
                <TableCell>{formatDate(inv.created)}</TableCell>
                {showSource && (
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <SourceBadge source={inv.source ?? 'matched'} />
                      {inv.deal_name && (
                        <span className="text-xs text-muted-foreground">{inv.deal_name}</span>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
              {isExpanded && (
                <TableRow>
                  <TableCell colSpan={showSource ? 7 : 6} className="bg-muted/40">
                    <HubSpotInvoiceDetail invoice={inv} />
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default HubSpotBillingDetail;
